import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { inputCls, btnPrimary, btnOutline, fmt, Label } from "@/components/portal/PortalUI";
import { GraduationCap, Send, Mail, ExternalLink, AlertTriangle } from "lucide-react";

const Info = ({ label, value, testid }) => (
  <div><p className="text-[11px] uppercase tracking-wide text-dark-text/50">{label}</p><p className="text-sm font-semibold text-navy" data-testid={testid}>{value || "—"}</p></div>
);

const EnrollForm = ({ applicantId, pathways, onDone }) => {
  const [opts, setOpts] = useState(null);
  const [f, setF] = useState({ programs: pathways, cohort: "", instructor_email: "", mentor_email: "", program_start_date: "", notes: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/portal-options").then((r) => setOpts(r.data)).catch(() => {}); }, []);
  const enroll = async () => {
    if (!window.confirm("Approve and enroll this applicant? A secure Student Portal invitation will be emailed.")) return;
    setBusy(true);
    const ins = opts?.instructors.find((i) => i.email === f.instructor_email);
    const men = opts?.mentors.find((i) => i.email === f.mentor_email);
    try {
      await api.post(`/admin/applicants/${applicantId}/enroll`, { ...f, instructor_name: ins?.name, mentor_name: men?.name });
      toast.success("Enrolled — registration invitation sent"); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-4 grid md:grid-cols-3 gap-3" data-testid="enroll-form">
      <div className="md:col-span-3 flex flex-wrap gap-4">
        {pathways.map((p) => (
          <label key={p} className="flex items-center gap-2 text-sm text-navy font-semibold">
            <input type="checkbox" className="accent-magenta" checked={f.programs.includes(p)} data-testid={`enroll-program-${p.split(" ")[0].toLowerCase()}`}
              onChange={(e) => setF({ ...f, programs: e.target.checked ? [...f.programs, p] : f.programs.filter((x) => x !== p) })} /> {p}
          </label>
        ))}
      </div>
      <div><Label>Class / cohort</Label><input value={f.cohort} onChange={(e) => setF({ ...f, cohort: e.target.value })} list="cohorts" className={inputCls} data-testid="enroll-cohort" />
        <datalist id="cohorts">{opts?.cohorts.map((c) => <option key={c} value={c} />)}</datalist></div>
      <div><Label>Instructor / trainer</Label><select value={f.instructor_email} onChange={(e) => setF({ ...f, instructor_email: e.target.value })} className={inputCls} data-testid="enroll-instructor">
        <option value="">— use dispatcher assignment / none —</option>{opts?.instructors.map((i) => <option key={i.email} value={i.email}>{i.label || i.name}</option>)}</select></div>
      {(() => { const t = opts?.instructors.find((i) => i.email === f.instructor_email); return t && (t.fully_booked || t.current_availability === "Unavailable" || t.current_availability === "Schedule Not Submitted") ? <p className="md:col-span-3 text-xs text-amber-700 font-semibold" data-testid="enroll-trainer-warning">Warning: {t.name} is {t.fully_booked ? "at capacity" : t.current_availability.toLowerCase()}. You may still proceed as an administrator override.</p> : null; })()}
      <div><Label>Mentor</Label><select value={f.mentor_email} onChange={(e) => setF({ ...f, mentor_email: e.target.value })} className={inputCls} data-testid="enroll-mentor">
        <option value="">— none —</option>{opts?.mentors.map((i) => <option key={i.email} value={i.email}>{i.name}</option>)}</select></div>
      <div><Label>Program start date</Label><input type="date" value={f.program_start_date} onChange={(e) => setF({ ...f, program_start_date: e.target.value })} className={inputCls} data-testid="enroll-start" /></div>
      <div className="md:col-span-2"><Label>Enrollment notes</Label><input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className={inputCls} data-testid="enroll-notes" /></div>
      <div className="md:col-span-3"><button onClick={enroll} disabled={busy || !f.programs.length} className={btnPrimary} data-testid="approve-and-enroll"><GraduationCap size={16} /> {busy ? "Enrolling…" : "Approve and Enroll"}</button></div>
    </div>
  );
};

export const EnrollPanel = ({ applicantId, reload }) => {
  const [st, setSt] = useState(null);
  const load = useCallback(() => api.get(`/admin/applicants/${applicantId}/enrollment`).then((r) => setSt(r.data)).catch(() => {}), [applicantId]);
  useEffect(() => { load(); }, [load]);
  if (!st) return null;
  const s = st.student;
  const act = async (path, msg) => { try { await api.post(`/admin/students/${s.student_id}/${path}`); toast.success(msg); load(); reload(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div className="bg-white rounded-lg border-2 border-magenta/30 shadow-sm p-5 mb-5" data-testid="enroll-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-display font-bold text-navy flex items-center gap-2"><GraduationCap className="text-magenta" size={20} /> Student Portal Enrollment</h3>
        {s && <Link to={`/admin/students/${s.student_id}`} className={btnOutline} data-testid="open-student-record"><ExternalLink size={15} /> Open Student Record</Link>}
      </div>
      {s ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            <Info label="Status" value="Enrolled Student" testid="enroll-status" />
            <Info label="Programs" value={s.programs.join(", ")} />
            <Info label="Enrolled / approved by" value={`${fmt(s.enrolled_at)} · ${s.approved_by}`} />
            <Info label="Registration" value={s.registration_status} testid="enroll-registration" />
            <Info label="Invitation sent" value={`${fmt(s.invitation?.sent_at)} (${s.invitation?.count || 0}x)`} />
            <Info label="Delivery status" value={s.invitation?.delivery_status} testid="enroll-delivery" />
            <Info label="Activated" value={fmt(s.activated_at)} />
            <Info label="First login" value={fmt(s.first_login_at)} />
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            {!s.activated_at && <button onClick={() => act("resend-invite", "Registration invitation re-sent")} className={btnPrimary} data-testid="resend-invite"><Send size={15} /> Resend Registration Invitation</button>}
            {s.activated_at && <button onClick={() => act("login-link", "One-time login link sent")} className={btnOutline} data-testid="send-login-link"><Mail size={15} /> Send One-Time Login Link</button>}
          </div>
        </>
      ) : st.eligible ? (
        <>
          <p className="text-sm text-dark-text/70 mt-2">This applicant meets every requirement for Student Portal access. Choose the programs and support team, then approve.</p>
          <EnrollForm applicantId={applicantId} pathways={st.portal_pathways} onDone={() => { load(); reload(); }} />
        </>
      ) : (
        <div className="mt-3 text-sm" data-testid="enroll-ineligible">
          <p className="flex items-center gap-2 text-amber-700 font-semibold"><AlertTriangle size={16} /> Not yet eligible for Approve and Enroll</p>
          <ul className="list-disc ml-6 mt-1 text-dark-text/70">{st.reasons.map((r) => <li key={r}>{r}</li>)}</ul>
          <p className="text-xs text-dark-text/50 mt-2">Student Portal access is available for the AAWTA Certified Dispatcher Program and Mentorship pathways (WGU and 160 Driving Academy are excluded in Phase 2).</p>
        </div>
      )}
    </div>
  );
};
